import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PageHeader } from '@/components/layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ImagePlus, Download, Loader2, Search, User, Users, Check, FileImage, FileText, Package, Film, Play, Images,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';
import { collateralApi } from '@/api/collateral';
import CollateralThumb from '@/components/CollateralThumb';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

// Download formats, keyed by the value the render API expects. Only the
// formats a template declares in `formats` are offered.
// Binding subjects for single-item generation. Each maps to a reference-data
// /list endpoint and the backend subject_type.
const SUBJECT_KINDS = [
  { key: 'agent', label: 'Agent', plural: 'agents' },
  { key: 'office', label: 'Office', plural: 'offices' },
  { key: 'partnership', label: 'Partnership', plural: 'partnerships' },
  { key: 'region', label: 'Region', plural: 'regions' },
  { key: 'listing', label: 'Listing', plural: 'listings' },
];

const FORMAT_META = {
  png: { label: 'PNG', icon: FileImage, hint: 'Best for social & web.' },
  pdf: { label: 'PDF', icon: FileText, hint: 'Print-ready, real page dimensions.' },
  mp4: { label: 'MP4', icon: Film, hint: 'Animated clip for Reels / Stories.' },
  gif: { label: 'GIF', icon: Images, hint: 'Animated GIF — plays inline in email.' },
};

/**
 * Collateral — generate on-brand collateral from a template.
 *
 * Pick a template, optionally bind an agent's live data, preview, and
 * download as PNG, PDF or MP4. This is the consumption face of the render
 * engine; template authoring (the editor) is a separate page.
 */
export default function Collateral() {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Single-mode binding subject (agent | office | partnership | region).
  // Note: the `agent*` state names hold whichever subject kind is selected.
  const [subjectKind, setSubjectKind] = useState('agent');
  const [agentQuery, setAgentQuery] = useState('');
  const debouncedAgentQuery = useDebouncedValue(agentQuery, 300);
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const [format, setFormat] = useState('png');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // MP4 clip preview (rendered on demand — heavier than the still preview).
  const [clipUrl, setClipUrl] = useState(null);
  const [renderingClip, setRenderingClip] = useState(false);

  const availableFormats = (selectedTemplate?.formats?.length
    ? selectedTemplate.formats
    : ['png']).filter((f) => FORMAT_META[f]);
  const isAnimated = format === 'mp4' || format === 'gif';
  const isGif = format === 'gif';

  // Single-agent vs whole-office (batch) mode.
  const [mode, setMode] = useState('single');
  const [officeQuery, setOfficeQuery] = useState('');
  const debouncedOfficeQuery = useDebouncedValue(officeQuery, 300);
  const [offices, setOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [batchDownloading, setBatchDownloading] = useState(false);

  // Ignore stale preview renders when selection changes quickly.
  const renderToken = useRef(0);
  // Track the active object URLs so we can revoke them and avoid leaks.
  const previewUrlRef = useRef(null);
  const clipUrlRef = useRef(null);

  const setPreview = useCallback((url) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = url;
    setPreviewUrl(url);
  }, []);

  const setClip = useCallback((url) => {
    if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
    clipUrlRef.current = url;
    setClipUrl(url);
  }, []);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
  }, []);

  // Load templates on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await collateralApi.listTemplates();
        if (cancelled) return;
        setTemplates(data?.data?.items || []);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load templates');
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Search the selected subject kind (agent/office/partnership/region).
  useEffect(() => {
    let cancelled = false;
    const q = debouncedAgentQuery.trim();
    if (q.length < 2) { setAgents([]); return; }
    const plural = (SUBJECT_KINDS.find((k) => k.key === subjectKind) || SUBJECT_KINDS[0]).plural;
    setLoadingAgents(true);
    (async () => {
      try {
        const res = await apiV1.get(`/reference-data/${plural}/list`, {
          params: { search: q, per_page: 20 },
        });
        if (!cancelled) setAgents(res.data?.data || []);
      } catch (e) {
        if (!cancelled) setAgents([]);
      } finally {
        if (!cancelled) setLoadingAgents(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedAgentQuery, subjectKind]);

  const switchSubjectKind = (k) => {
    setSubjectKind(k);
    setSelectedAgent(null);
    setAgentQuery('');
    setAgents([]);
  };

  // Keep the selected format valid for the chosen template (e.g. an
  // animated template only offers MP4).
  useEffect(() => {
    if (selectedTemplate && !availableFormats.includes(format)) {
      setFormat(availableFormats[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  // Re-render the (PNG) still preview whenever the template or agent changes.
  // The MP4 clip preview is rendered on demand, so drop any stale clip here.
  useEffect(() => {
    setClip(null);
    if (!selectedTemplate) { setPreview(null); return; }
    const token = ++renderToken.current;
    setRendering(true);
    (async () => {
      try {
        const blob = await collateralApi.render({
          templateId: selectedTemplate.id,
          subjectType: subjectKind, subjectId: selectedAgent?.id || null,
          format: 'png',
        });
        if (token !== renderToken.current) return; // superseded
        setPreview(URL.createObjectURL(blob));
      } catch (e) {
        if (token === renderToken.current) {
          setPreview(null);
          toast.error('Could not render preview');
        }
      } finally {
        if (token === renderToken.current) setRendering(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, selectedAgent]);

  // Office search (batch mode).
  useEffect(() => {
    let cancelled = false;
    const q = debouncedOfficeQuery.trim();
    if (q.length < 2) { setOffices([]); return; }
    (async () => {
      try {
        const res = await apiV1.get('/reference-data/offices/list', { params: { search: q, per_page: 20 } });
        if (!cancelled) setOffices(res.data?.data || []);
      } catch (e) {
        if (!cancelled) setOffices([]);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedOfficeQuery]);

  const switchMode = (m) => {
    setMode(m);
    if (m === 'office') { setSelectedAgent(null); setAgentQuery(''); }
    else { setSelectedOffice(null); setOfficeQuery(''); }
  };

  const handleDownload = async () => {
    if (!selectedTemplate) return;
    setDownloading(true);
    try {
      const blob = await collateralApi.render({
        templateId: selectedTemplate.id,
        subjectType: subjectKind, subjectId: selectedAgent?.id || null,
        format,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const slug = selectedTemplate.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      a.href = url;
      a.download = `${slug || 'collateral'}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch (e) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handlePreviewClip = async () => {
    if (!selectedTemplate) return;
    setRenderingClip(true);
    try {
      const blob = await collateralApi.render({
        templateId: selectedTemplate.id,
        subjectType: subjectKind, subjectId: selectedAgent?.id || null,
        format,
      });
      setClip(URL.createObjectURL(blob));
    } catch (e) {
      toast.error('Could not render clip');
    } finally {
      setRenderingClip(false);
    }
  };

  const handleBatchDownload = async () => {
    if (!selectedTemplate || !selectedOffice) return;
    setBatchDownloading(true);
    try {
      const { blob, count, truncated } = await collateralApi.batch({
        templateId: selectedTemplate.id,
        officeId: selectedOffice.id,
        format,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const slug = selectedTemplate.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const office = (selectedOffice.name || 'office').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      a.href = url;
      a.download = `${slug || 'collateral'}-${office}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Generated ${count} file${count !== 1 ? 's' : ''}${truncated ? ' (capped at 200)' : ''}`);
    } catch (e) {
      const code = e?.response?.status;
      toast.error(code === 400 ? 'No agents found for that office' : 'Batch generation failed');
    } finally {
      setBatchDownloading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="collateral-page">
      <PageHeader
        title="Collateral"
        description="Generate on-brand collateral from a template, with an agent's live details filled in."
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: template gallery */}
        <div className="lg:col-span-5 space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            1 · Choose a template
          </h2>
          {loadingTemplates ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm p-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
            </div>
          ) : templates.length === 0 ? (
            <Card><CardContent className="p-6 text-sm text-muted-foreground">
              No templates yet.
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {templates.map((t) => {
                const active = selectedTemplate?.id === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedTemplate(t)}
                    data-testid={`template-card-${t.id}`}
                    className={`w-full text-left rounded-lg border p-4 transition-colors ${
                      active
                        ? 'border-primary ring-1 ring-primary bg-primary/5'
                        : 'border-border hover:border-primary/50 hover:bg-muted/40'
                    }`}
                  >
                    <div className="flex gap-3">
                      <CollateralThumb
                        templateId={t.id}
                        animated={(t.formats || []).includes('mp4')}
                        className="h-16 w-16 rounded-md border shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <span className="font-medium truncate">{t.name}</span>
                          {active && <Check className="h-4 w-4 text-primary shrink-0" />}
                        </div>
                        {t.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-[11px] capitalize">{t.category}</Badge>
                          {t.artboard && (
                            <span className="text-[11px] text-muted-foreground">
                              {t.artboard.width}×{t.artboard.height}
                            </span>
                          )}
                          {(t.formats || []).map((f) => (
                            <span key={f} className="text-[11px] text-muted-foreground uppercase">{f}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: details + preview */}
        <div className="lg:col-span-7 space-y-4">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            2 · Add details &amp; download
          </h2>

          {!selectedTemplate ? (
            <Card><CardContent className="p-10 text-center text-muted-foreground">
              <ImagePlus className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Choose a template on the left to start.</p>
            </CardContent></Card>
          ) : (
            <Card>
              <CardContent className="p-4 space-y-4">
                {/* Mode toggle */}
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Generate for</label>
                  <div className="inline-flex rounded-md border p-0.5">
                    <button type="button" onClick={() => switchMode('single')} data-testid="mode-single"
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded ${mode === 'single' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}>
                      <User className="h-3.5 w-3.5" /> Single agent
                    </button>
                    <button type="button" onClick={() => switchMode('office')} data-testid="mode-office"
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded ${mode === 'office' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}>
                      <Users className="h-3.5 w-3.5" /> Whole office
                    </button>
                  </div>
                </div>

                {/* Subject picker (single mode): agent / office / partnership / region */}
                {mode === 'single' && (
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Bind to</label>
                    <div className="inline-flex flex-wrap rounded-md border p-0.5 mb-2">
                      {SUBJECT_KINDS.map((k) => (
                        <button
                          key={k.key}
                          type="button"
                          onClick={() => switchSubjectKind(k.key)}
                          data-testid={`subject-${k.key}`}
                          className={`px-2.5 py-1 text-xs rounded ${subjectKind === k.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
                        >
                          {k.label}
                        </button>
                      ))}
                    </div>
                    {selectedAgent ? (
                      <div className="flex items-center justify-between rounded-md border px-3 py-2">
                        <span className="flex items-center gap-2 text-sm">
                          <User className="h-4 w-4 text-primary" /> {selectedAgent.name}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedAgent(null); setAgentQuery(''); }}>
                          Change
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            className="pl-9"
                            placeholder={`Search ${subjectKind}s by name…`}
                            value={agentQuery}
                            onChange={(e) => setAgentQuery(e.target.value)}
                            data-testid="agent-search"
                          />
                        </div>
                        {loadingAgents && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
                            <Loader2 className="h-3 w-3 animate-spin" /> Searching…
                          </div>
                        )}
                        {agents.length > 0 && (
                          <div className="mt-2 border rounded-md divide-y max-h-52 overflow-y-auto">
                            {agents.map((a) => (
                              <button
                                key={a.id}
                                type="button"
                                onClick={() => setSelectedAgent(a)}
                                data-testid={`agent-option-${a.id}`}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                              >
                                <User className="h-3.5 w-3.5 text-muted-foreground" /> {a.name}
                              </button>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-1.5">
                          Optional — preview shows the template's static content until a {subjectKind} is chosen.
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Office picker (batch mode) */}
                {mode === 'office' && (
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Office</label>
                    {selectedOffice ? (
                      <div className="flex items-center justify-between rounded-md border px-3 py-2">
                        <span className="flex items-center gap-2 text-sm">
                          <Users className="h-4 w-4 text-primary" /> {selectedOffice.name}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedOffice(null); setOfficeQuery(''); }}>
                          Change
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            className="pl-9"
                            placeholder="Search offices…"
                            value={officeQuery}
                            onChange={(e) => setOfficeQuery(e.target.value)}
                            data-testid="office-search"
                          />
                        </div>
                        {offices.length > 0 && (
                          <div className="mt-2 border rounded-md divide-y max-h-52 overflow-y-auto">
                            {offices.map((o) => (
                              <button
                                key={o.id}
                                type="button"
                                onClick={() => setSelectedOffice(o)}
                                data-testid={`office-option-${o.id}`}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                              >
                                <Users className="h-3.5 w-3.5 text-muted-foreground" /> {o.name}
                              </button>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-1.5">
                          One file per active agent in the office, delivered as a ZIP (max 200).
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Format toggle — driven by the template's declared formats */}
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Download format</label>
                  <div className="inline-flex rounded-md border p-0.5">
                    {availableFormats.map((f) => {
                      const meta = FORMAT_META[f];
                      const Icon = meta.icon;
                      return (
                        <button
                          key={f}
                          type="button"
                          onClick={() => setFormat(f)}
                          data-testid={`format-${f}`}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded ${
                            format === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                          }`}
                        >
                          <Icon className="h-3.5 w-3.5" /> {meta.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {FORMAT_META[format]?.hint} {isAnimated ? 'Still shown below — press Preview clip to play it.' : 'Preview is shown as PNG.'}
                  </p>
                </div>

                {/* Preview */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-medium">Preview</label>
                    {isAnimated && mode === 'single' && (
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={handlePreviewClip}
                        disabled={renderingClip}
                        data-testid="preview-clip-btn"
                      >
                        {renderingClip ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}
                        Preview {isGif ? 'GIF' : 'clip'}
                      </Button>
                    )}
                  </div>
                  <div className="relative rounded-md border bg-muted/30 flex items-center justify-center min-h-[260px] overflow-hidden">
                    {(rendering || renderingClip) && (
                      <div className="absolute inset-0 flex items-center justify-center bg-background/60 z-10">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      </div>
                    )}
                    {isAnimated && clipUrl ? (
                      isGif ? (
                        <img
                          src={clipUrl}
                          alt={`${selectedTemplate.name} animated preview`}
                          data-testid="preview-clip"
                          className="max-h-[420px] w-auto object-contain"
                        />
                      ) : (
                        <video
                          src={clipUrl}
                          data-testid="preview-clip"
                          className="max-h-[420px] w-auto object-contain"
                          autoPlay loop muted playsInline controls
                        />
                      )
                    ) : previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={`${selectedTemplate.name} preview`}
                        data-testid="preview-image"
                        className="max-h-[420px] w-auto object-contain"
                      />
                    ) : (
                      !rendering && <span className="text-sm text-muted-foreground p-8">No preview</span>
                    )}
                  </div>
                  {isAnimated && (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      The still above is the resting frame; the {isGif ? 'GIF' : 'MP4'} animates on export.
                    </p>
                  )}
                </div>

                {mode === 'single' ? (
                  <Button
                    onClick={handleDownload}
                    disabled={downloading || rendering}
                    className="w-full"
                    data-testid="download-btn"
                  >
                    {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                    Download {format.toUpperCase()}
                  </Button>
                ) : (
                  <Button
                    onClick={handleBatchDownload}
                    disabled={!selectedOffice || batchDownloading}
                    className="w-full"
                    data-testid="batch-download-btn"
                  >
                    {batchDownloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Package className="h-4 w-4 mr-2" />}
                    Generate all for office · ZIP
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
